import { memo, useState, useEffect, useRef } from 'react';
import { useStore } from 'store/StoreContext';
import { useHistory, useLocation } from 'react-router-dom';
import { callUpdateUserApi, callCheckOnetimePasswordApi, CheckOnetimePasswordResData } from 'requests/RequestUser';
import { RoutePath } from 'consts/RoutePath';
import { CompleteDialog } from 'components/organisms/CompleteDialog';
import { UpdateUserReqData } from 'requests/RequestUser';
import { CircularProgress } from '@material-ui/core';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { ApiError } from 'requests/RequestBase';
import { ApiErrorCode, findErrorMessageByApiError } from 'consts/ApiErrorCode';
import {
	GetLoginDisplayItemResData,
	callGetLoginDisplayItemApi,
	CommonLoginV2ResData,
	RequiredProfileEditRedirectResData,
	callCommonLoginV2AfterRequiredProfileEditApi,
} from 'requests/RequestLogin';
import {
	convertObjectToGetParameter,
	convertObjectToGetParameterWithEncode,
} from 'common/ObjectToGetParameterConverter';

type Props = {};

export const ProfileEditMailAddressConfirmTemplate = memo((_props: Props) => {
	const [store] = useStore();
	const history = useHistory<UpdateUserReqData>();
	const location = useLocation();
	const search = location.search;
	const queryParams = new URLSearchParams(search);
	const isRequiredProfile = queryParams.get('isRequiredProfile')
		? 'true' === queryParams.get('isRequiredProfile')
		: false;
	const [formData, setFormData] = useState({ onetime_password: '' });
	const [formErrors, setFormErrors] = useState({ onetime_password: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
	const authorizeV2Data = store.authorizeV2Data;
	const [loginDisplayItem, setLoginDisplayItem] = useState<GetLoginDisplayItemResData>(undefined);
	const [actionError, setActionError] = useState('');
	const handleCompleteDialogOpen = () => {
		setIsCompleteDialogOpen(true);
	};
	const handleCompleteDialogClose = async () => {
		setIsCompleteDialogOpen(false);
		if (isRequiredProfile) {
			setProcessLoading(true);
			// commonloginv2の続きからのAPIを叩く
			const result = await callCommonLoginV2AfterRequiredProfileEditApi(authorizeV2Data);
			if (result.status === HttpStatus.REDIRECT) {
				const redirectData = result.data as RequiredProfileEditRedirectResData;
				const getParameters = convertObjectToGetParameterWithEncode(
					store.authorizeV2Data,
					'code_challenge',
					'redirect_uri',
					'nonce',
				);
				if (redirectData.type === 'attribute') {
					history.push(`${RoutePath.ATTRIBUTE_AGREEMENT}?${getParameters}`);
				}
				return;
			}
			if (result.status !== HttpStatus.OK) {
				setActionError(ReactError.E9999);
				history.push(RoutePath.ERROR);
				return;
			}
			const commonLoginV2ResData = result.data as CommonLoginV2ResData;
			const getParameters = convertObjectToGetParameter(commonLoginV2ResData);
			window.location.href = `${
				authorizeV2Data.redirect_uri ? decodeURIComponent(authorizeV2Data.redirect_uri) : loginDisplayItem.redirect_url
			}?${getParameters}`;
			// setProcessLoading(false);
		} else {
			history.push(RoutePath.MY_PAGE);
			setProcessLoading(false);
		}
	};
	const [processLoading, setProcessLoading] = useState(false);

	// ワンタイムパスワード入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, onetime_password: event.target.value }));
	};

	// 確認ボタンのonClickで呼ぶ
	const checkOnetimePassword = async () => {
		setProcessLoading(true);

		// エラー解除
		setFormErrors((prev) => ({ ...prev, onetime_password: '' }));

		// ワンタイムパスワード検証
		if (!formData.onetime_password.trim()) {
			setFormErrors((prev) => ({ ...prev, onetime_password: ReactError.E0001 }));
			setProcessLoading(false);
			return;
		}
		const checkOnetimePasswordResult = await callCheckOnetimePasswordApi({
			mailaddress: history.location.state.data.mailaddress_1,
			onetimepass: formData.onetime_password,
		});
		if (checkOnetimePasswordResult.status !== HttpStatus.OK) {
			const error = checkOnetimePasswordResult.data as ApiError;
			if (error.error_code === ApiErrorCode.E1157.code) {
				setFormErrors((prev) => ({ ...prev, onetime_password: findErrorMessageByApiError(error) }));
				setProcessLoading(false);
				return;
			}
			history.push(RoutePath.ERROR);
			return;
		}
		const data = checkOnetimePasswordResult.data as CheckOnetimePasswordResData;
		if (!data.exist) {
			setFormErrors((prev) => ({ ...prev, onetime_password: 'ワンタイムパスワードが正しくありません。' }));
			setProcessLoading(false);
			return;
		}

		if (!history.location.state.data.gid) {
			history.push(RoutePath.ERROR);
			return;
		}
		const result = await callUpdateUserApi(history.location.state);
		if (result.status !== HttpStatus.OK) {
			history.push(RoutePath.ERROR);
			return;
		}

		if (isRequiredProfile) {
			const getDisplayItemResult = await callGetLoginDisplayItemApi({
				gid_dp: authorizeV2Data.gid_dp,
				site_cd: authorizeV2Data.client_id,
			});
			if (getDisplayItemResult.status !== HttpStatus.OK || !getDisplayItemResult.data) {
				setActionError(ReactError.E0008);
				history.push(RoutePath.ERROR);
				return;
			}
			setLoginDisplayItem(getDisplayItemResult.data);
		}
		handleCompleteDialogOpen();
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDプロフィール変更｜認証コード入力';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors]);

	return (
		<>
			<section className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="section-head">プロフィール変更</div>
					<div className="section-txt-box">
						<div className="txt-head">メール受信の確認</div>
						<p>
							ご指定のメールアドレスに、メールをお送りいたしました。
							<br />
							メールに記載されたワンタイムパスワードを入力してください。
						</p>
					</div>

					{isFormError ? (
						<div className="error-box" ref={errorRef}>
							<>
								<p>※下記項目は全て必須となります。</p>
								<div className="error-box-in">
									{formErrors.onetime_password !== '' ? (
										<p>【ワンタイムパスワード】{formErrors.onetime_password}</p>
									) : null}
								</div>
							</>
						</div>
					) : null}

					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">ワンタイムパスワード</label>
						</div>
						<div className="form-input">
							<input
								type="password"
								name="onetime_password"
								value={formData.onetime_password}
								onChange={handleChangeValue}
							/>
						</div>
						<p className="error">{formErrors.onetime_password}</p>
					</div>
					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={checkOnetimePassword}>確認</button>}
						</div>
					</div>
				</div>
			</section>
			{/* 完了ダイアログ */}
			<CompleteDialog
				isOpen={isCompleteDialogOpen}
				doTrans={handleCompleteDialogClose}
				buttonLabel={loginDisplayItem?.title}
			/>
		</>
	);
});
